import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button, Grid, Typography, useMediaQuery } from "@mui/material";
import toast from "react-hot-toast";
import { PageTitle } from "../../../components/PageTitle";
import { ReactComponent as BinIcon } from "../../../assets/svg/bin.svg";
import { ReactComponent as DuplicateIcon } from "../../../assets/svg/duplicate.svg";
import axiosInstant from "../../../api";
import ShortlistFormModal from "../Shortlists/ShortlistFormModal";
import DeleteShortlistModal from "../Shortlists/DeleteShortListModal";
import { IconShare, IconPencil, IconVideo } from "../../../utils/icons";
import { MainContainer } from "../../../components/ContentWrapper";
import { ConfirmationDialog } from "../../../components/ConfirmationDialog/ConfirmationDialog";
import { DotLottleLoader } from "../../../components/Loader/DotLottleLoader";
import { LoadMoreBtn, PageDivider } from "../../../components/Loader";
import { DropdownMenu } from "../../../components/Dropdown/Dropdown";
import { MenuIcon } from "../../../components/MenuOptions/MenuIcon";
import { ViewToggle } from "../../../components/ViewToggle/ViewToggle";
import {
  videoShortlistById,
  videoShortlistVideos,
  videoShortlistVideosOrder,
  removeVideoFromVideoShortlist,
} from "../../../api/endpoints";
import { VideoCard } from "../../../components/videoSection/VideoCard";
import { UploadableVideoCard } from "../../../components/videoSection/VideoUpload";
import { useCommonParams } from "../../../hooks/useCommonParams";
import { usePagination } from "../../../hooks/usePagination";
import { useReorderMode } from "../../../hooks/useReorderMode";
import { useUser } from "../../../hooks/useUser";
import { isInternal } from "../../../utils";
import * as Reorder from "../../../components/dnd/Reorder";
import { ReorderVideoRow } from "./ReorderVideoRow";
import VideoPlayerDialog from "../../../components/VideoPlayerDialog/VideoPlayerDialog";
import { slideRight } from "../../pro/Listing/components/helper";

const PAGE_SIZE = 20;

export default function VideoShortlistDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { role } = useUser();

  const {
    searchParams,
    setSearchParams,
    page,
    setPage,
    view: _view,
    setView,
  } = useCommonParams({ defaultView: "list" });
  const isMobile = useMediaQuery("(max-width: 599.95px)");
  const view = isMobile ? "grid" : (_view as "grid" | "list");

  const [shortlist, setShortlist] = useState<any>(null);
  const [shortlistName, setShortlistName] = useState("");
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [isFetchingMeta, setIsFetchingMeta] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState<any>(null);

  // Fetch shortlist metadata
  const fetchShortlistMeta = useCallback(async () => {
    if (!id) return;
    setIsFetchingMeta(true);
    try {
      const response = await axiosInstant.get(videoShortlistById(parseInt(id)));
      setShortlistName(response.data?.name);
      setShortlist(response.data);
    } catch (error) {
      console.error("Error fetching video shortlist:", error);
      setShortlistName("Video Shortlist");
    } finally {
      setIsFetchingMeta(false);
    }
  }, [id]);

  useEffect(() => {
    fetchShortlistMeta();
  }, [fetchShortlistMeta]);

  const fetchFn = useCallback(
    async (pageNum: number) => {
      if (!id) return { rows: [], count: 0, error: null };
      const offset = (pageNum - 1) * PAGE_SIZE;
      try {
        const response = await axiosInstant.get(videoShortlistVideos(parseInt(id)), {
          params: { limit: PAGE_SIZE, offset },
        });
        const { rows, count } = response.data;
        return { rows: rows ?? [], count: count ?? 0, error: null };
      } catch (e: any) {
        if (e?.name === 'CanceledError' || e?.name === 'AbortError') {
          return { rows: [], count: 0, error: null };
        }
        return { rows: [], count: 0, error: e?.message || "Failed to load videos" };
      }
    },
    [id],
  );

  const {
    items: videos,
    totalCount,
    loading,
    hasMore,
    canLoadPrevious,
    loadingPrevious,
    initLoad,
    loadMore,
    loadPrevious,
    topSentinelRef,
  } = usePagination({
    fetchFn,
    pageSize: PAGE_SIZE,
    deepLink: {
      page,
      setPage,
      searchParams,
      setSearchParams,
      ignoreParams: ["page", "view"],
    },
  });

  // Reorder mode works on its own full, unpaginated copy of the shortlist —
  // it never touches the pagination state or the URL.
  const reorderMode = useReorderMode<any>({
    fetchAll: useCallback(async () => {
      if (!id) return [];
      const response = await axiosInstant.get(videoShortlistVideos(parseInt(id, 10)), {
        params: { limit: 1000, offset: 0 },
      });
      return response.data?.rows ?? [];
    }, [id]),
    saveOrder: useCallback(
      async (orderedVideos: any[]) => {
        await axiosInstant.patch(videoShortlistVideosOrder(parseInt(id!, 10)), {
          order: orderedVideos.map((video) => video.id),
        });
      },
      [id],
    ),
    onSaved: useCallback(() => {
      setPage(1);
      initLoad(1);
    }, [setPage, initLoad]),
  });

  const removeVideo = async (video: any) => {
    if (!shortlist?.id) return;
    try {
      await axiosInstant.delete(removeVideoFromVideoShortlist(shortlist.id, video.id));
      toast.success("Video removed from shortlist.");
      fetchShortlistMeta();
      initLoad(page);
    } catch (error) {
      toast.error("Failed to remove video. Try again.");
    }
  };

  const clearShortlist = async () => {
    if (!shortlist?.id) return;
    try {
      setIsClearing(true);
      await axiosInstant.delete(`/video-shortlists/${shortlist.id}/clear`);
      toast.success("Video shortlist cleared.");
      fetchShortlistMeta();
      initLoad(1);
    } catch (error) {
      toast.error("Failed to clear the shortlist.");
    } finally {
      setIsClearing(false);
      setShowClearConfirm(false);
    }
  };

  const share = () => {
    if (!shortlist?.id) return;
    window.open(`/videos/shortlists/share/${shortlist.id}`, "_blank");
  };

  if (!id) return null;

  return (
    <MainContainer>
      <Box className="flex flex-col gap-6">
        {isFetchingMeta && !shortlist ? (
          <DotLottleLoader />
        ) : (
          <div className="mastcontent mb-[100px]">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 flex-wrap">
              {shortlistName && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: "8px", sm: "12px" },
                    flexWrap: "wrap",
                    minWidth: 0,
                  }}
                >
                  <PageTitle title={shortlistName} />
                  <Box
                    sx={{
                      display: "flex",
                      padding: { xs: "2px 10px", sm: "2px 16px" },
                      justifyContent: "center",
                      alignItems: "center",
                      borderRadius: "50px",
                      background: "var(--tint)",
                      color: "var(--accent-text)",
                      fontFamily: '"CircularStd"',
                      fontSize: { xs: "12px", sm: "14px" },
                      fontWeight: 900,
                      marginTop: "3px",
                    }}
                  >
                    Video Shortlist
                  </Box>
                </Box>
              )}

              <div className="flex items-center gap-2 justify-end">
                {reorderMode.active ? (
                  <>
                    <Button
                      variant="outlined"
                      sx={{ textTransform: "none" }}
                      onClick={reorderMode.cancel}
                      disabled={reorderMode.saving}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      sx={{ textTransform: "none" }}
                      onClick={reorderMode.save}
                      disabled={reorderMode.saving || reorderMode.loading}
                    >
                      {reorderMode.saving ? "Saving..." : "Save Order"}
                    </Button>
                  </>
                ) : (
                  <>
                    {!isMobile && (
                      <ViewToggle
                        view={view}
                        onViewChange={(v) => setView(v as "grid" | "list")}
                      />
                    )}

                    {isInternal(role) && totalCount > 1 && (
                      <Button
                        variant="outlined"
                        sx={{
                          textTransform: "none",
                          whiteSpace: "nowrap",
                          px: { xs: 1.5, sm: 2 },
                          fontSize: { xs: 13, sm: 14 },
                        }}
                        onClick={reorderMode.start}
                      >
                        Reorder
                      </Button>
                    )}

                    {Boolean(videos.length) && (
                      <Button
                        variant="outlined"
                        sx={{
                          textTransform: "none",
                          whiteSpace: "nowrap",
                          px: { xs: 1.5, sm: 2 },
                          fontSize: { xs: 13, sm: 14 },
                        }}
                        onClick={() => setShowClearConfirm(true)}
                      >
                        Clear shortlist
                      </Button>
                    )}

                    <DropdownMenu
                      trigger={<MenuIcon id="video-shortlist-menu" isDetailedPage />}
                      options={[
                        {
                          label: "Share",
                          onClick: share,
                          icon: IconShare,
                        },
                        {
                          label: "Edit",
                          onClick: () => setIsEditModalOpen(true),
                          icon: IconPencil,
                        },
                        {
                          label: "Duplicate",
                          onClick: () => setDuplicateModalOpen(true),
                          icon: DuplicateIcon,
                        },
                        {
                          label: "Delete",
                          onClick: () => setIsDeleteModalOpen(true),
                          icon: BinIcon,
                        },
                      ]}
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      transformOrigin={{ vertical: "top", horizontal: "right" }}
                    />
                  </>
                )}
              </div>
            </div>

            <PageDivider
              count={totalCount}
              label="Videos"
              className="mt-5"
              canLoadPrevious={canLoadPrevious && !reorderMode.active}
              hasMore={hasMore && !reorderMode.active}
              onLoadPrevious={loadPrevious}
            />

            {reorderMode.active ? (
              reorderMode.loading ? (
                <div className="flex justify-center items-center py-20">
                  <DotLottleLoader fill={false} />
                </div>
              ) : (
                <Reorder.Root options={reorderMode.items} onUpdateOrder={reorderMode.reorder}>
                  <Box className="flex flex-col gap-3 my-8">
                    {reorderMode.items.map((video: any, index: number) => (
                      <ReorderVideoRow key={video.id} video={video} index={index} />
                    ))}
                  </Box>
                </Reorder.Root>
              )
            ) : loading && videos.length === 0 ? (
              <div className="flex justify-center items-center py-20">
                <DotLottleLoader fill={false} />
              </div>
            ) : videos.length > 0 ? (
              <div className="my-8">
                {canLoadPrevious && <div ref={topSentinelRef} style={{ height: 1 }} />}
                {loadingPrevious && (
                  <div className="flex justify-center py-2">
                    <DotLottleLoader fill={false} sx={{ width: 100, height: 100 }} />
                  </div>
                )}

                {view === "list" ? (
                  <Box className="flex flex-col gap-3">
                    {videos.map((video: any) => (
                      <UploadableVideoCard
                        key={video.id}
                        video={video}
                        project={video.project}
                        refetch={() => { fetchShortlistMeta(); initLoad(page); }}
                      >
                        {/* drag-only: the shortlist feed carries no project */}
                        {() => (
                          <VideoCard
                            video={video}
                            variant="row"
                            refetch={() => { fetchShortlistMeta(); initLoad(page); }}
                            onOpen={() => setSelectedVideo(video)}
                            menuContext={{ inShortlistDetail: true }}
                            onRemoveFromVideoShortlist={() => removeVideo(video)}
                          />
                        )}
                      </UploadableVideoCard>
                    ))}
                  </Box>
                ) : (
                  <Grid container spacing={2} columns={{ xs: 1, sm: 2, md: 3, lg: 4, xl: 5 }}>
                    {videos.map((video: any) => (
                      <Grid item xs={1} key={video.id} className="mt-2">
                        <UploadableVideoCard
                          video={video}
                          project={video.project}
                          refetch={() => { fetchShortlistMeta(); initLoad(page); }}
                        >
                          {/* drag-only: the shortlist feed carries no project */}
                          {() => (
                            <VideoCard
                              video={video}
                              variant="browse"
                              refetch={() => { fetchShortlistMeta(); initLoad(page); }}
                              onOpen={() => setSelectedVideo(video)}
                              menuContext={{ inShortlistDetail: true }}
                              onRemoveFromVideoShortlist={() => removeVideo(video)}
                            />
                          )}
                        </UploadableVideoCard>
                      </Grid>
                    ))}
                  </Grid>
                )}

                {hasMore && (
                  <LoadMoreBtn onClick={loadMore} loading={loading} customClasses="!my-4" />
                )}
              </div>
            ) : (
              <Box className="flex flex-col items-center justify-center gap-4" sx={{ minHeight: 'calc(100vh - 300px)' }}>
                {/* stroke is an SVG presentation attribute — cannot take var(); left literal */}
                <IconVideo size={48} stroke="#B0B0B0" />
                <Typography variant="body1" color="textSecondary">
                  No videos in this shortlist yet. Add videos from the video menu.
                </Typography>
                <Button
                  variant="contained"
                  sx={{ textTransform: "none" }}
                  onClick={() => navigate("/videos")}
                  endIcon={
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-block',
                        animation: `${slideRight} 1s ease-in-out infinite`,
                      }}
                    >
                      &rarr;
                    </Box>
                  }
                >
                  Go to Videos
                </Button>
              </Box>
            )}

            <ShortlistFormModal
              mode="edit"
              open={isEditModalOpen}
              setOpen={setIsEditModalOpen}
              shortlistId={shortlist?.id}
              currentName={shortlistName}
              updateShortlistName={setShortlistName}
              fetchShortlists={() => {}}
              apiBase="/video-shortlists"
              label="Video Shortlist"
              currentDownloadEnabled={shortlist?.download_enabled ?? true}
              onDownloadEnabledChange={(enabled) => setShortlist((prev: any) => prev ? { ...prev, download_enabled: enabled } : prev)}
            />

            <ConfirmationDialog
              open={showClearConfirm}
              onClose={() => setShowClearConfirm(false)}
              onConfirm={clearShortlist}
              title="Clear Video Shortlist"
              description="Are you sure you want to remove all videos from this shortlist?"
              disabled={isClearing}
            />

            <DeleteShortlistModal
              open={isDeleteModalOpen}
              setOpen={setIsDeleteModalOpen}
              shortlistId={shortlist?.id}
              fetchShortlists={() => navigate("/videos/shortlists")}
              apiBase="/video-shortlists"
              label="Video Shortlist"
            />

            {duplicateModalOpen && (
              <ShortlistFormModal
                mode="duplicate"
                open={duplicateModalOpen}
                setOpen={setDuplicateModalOpen}
                shortlistId={shortlist?.id}
                currentName={shortlist?.name}
                onShortlistCreated={() => {}}
                apiBase="/video-shortlists"
                label="Video Shortlist"
              />
            )}

            <VideoPlayerDialog
              open={!!selectedVideo}
              onClose={() => setSelectedVideo(null)}
              video={selectedVideo}
            />
          </div>
        )}
      </Box>
    </MainContainer>
  );
}
